// src/pages/Departments.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [selectedDept, setSelectedDept] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showFacultyDropdown, setShowFacultyDropdown] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [uploadingType, setUploadingType] = useState('');

  // Alert state
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: ''
  });

  // Department form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    seats: '',
    duration: '',
    facultyCount: '',
    achievements: '',
    image: '',
    headOfDepartment: '',
    email: '',
    phone: '',
    established: '',
    hodMessage: ''
  });

  // Notices state
  const [notices, setNotices] = useState([]);
  const [noticeForm, setNoticeForm] = useState({
    title: '',
    description: '',
    file: null
  });

  // Labs state
  const [labs, setLabs] = useState([]);
  const [labForm, setLabForm] = useState({
    name: '',
    description: '',
    equipment: '',
    images: []
  });
  const [labImageFiles, setLabImageFiles] = useState([]);
  const [labImagePreviews, setLabImagePreviews] = useState([]);

  // Gallery state
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const deptUnsubscribe = onSnapshot(collection(db, 'departments'), (snapshot) => {
      const deptData = [];
      snapshot.forEach((doc) => {
        deptData.push({ id: doc.id, ...doc.data() });
      });
      setDepartments(deptData);
      if (selectedDept) {
        const updated = deptData.find(d => d.id === selectedDept.id);
        if (updated) {
          setSelectedDept(updated);
          if (updated.notices) setNotices(updated.notices);
          if (updated.labs) setLabs(updated.labs);
          if (updated.gallery) setGalleryImages(updated.gallery);
        }
      }
      setLoading(false);
    });

    const facultyUnsubscribe = onSnapshot(collection(db, 'faculty'), (snapshot) => {
      const facultyData = [];
      snapshot.forEach((doc) => {
        facultyData.push({ id: doc.id, ...doc.data() });
      });
      setFaculty(facultyData);
    });

    return () => {
      clearInterval(clockInterval);
      deptUnsubscribe();
      facultyUnsubscribe();
    };
  }, []);

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'success', message: '' }), 4000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      seats: '',
      duration: '',
      facultyCount: '',
      achievements: '',
      image: '',
      headOfDepartment: '',
      email: '',
      phone: '',
      established: '',
      hodMessage: ''
    });
    setImageFile(null);
    setImagePreview('');
    setEditingDept(null);
    setUploadProgress(0);
    setIsUploading(false);
    setShowFacultyDropdown(false);
  };

  const resetNotices = () => setNotices([]);
  const resetLabs = () => setLabs([]);
  const resetGallery = () => setGalleryImages([]);

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name || '',
      description: dept.description || '',
      seats: dept.seats || '',
      duration: dept.duration || '',
      facultyCount: dept.facultyCount || '',
      achievements: dept.achievements || '',
      image: dept.image || '',
      headOfDepartment: dept.headOfDepartment || '',
      email: dept.email || '',
      phone: dept.phone || '',
      established: dept.established || '',
      hodMessage: dept.hodMessage || ''
    });
    if (dept.image) setImagePreview(dept.image);
    setModalOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileUpload = async (file, path) => {
    if (!file) return null;
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleMultipleImageChange = (e, setFiles, setPreviews) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setFiles(files);
    const previews = [];
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        previews.push(event.target.result);
        if (previews.length === files.length) {
          setPreviews([...previews]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let imageUrl = formData.image;
      if (imageFile) {
        imageUrl = await handleFileUpload(imageFile, 'departments/images');
      }

      const deptData = {
        name: formData.name,
        description: formData.description,
        seats: formData.seats,
        duration: formData.duration,
        facultyCount: formData.facultyCount,
        achievements: formData.achievements,
        image: imageUrl,
        headOfDepartment: formData.headOfDepartment,
        email: formData.email,
        phone: formData.phone,
        established: formData.established,
        hodMessage: formData.hodMessage,
        notices: notices,
        labs: labs,
        gallery: galleryImages,
        updatedAt: new Date()
      };

      if (editingDept) {
        if (formData.image && imageFile && formData.image !== imageUrl) {
          try { await deleteObject(ref(storage, formData.image)); } catch (e) {}
        }
        await updateDoc(doc(db, 'departments', editingDept.id), deptData);
        showAlert('success', 'Department updated successfully!');
        const updated = departments.find(d => d.id === editingDept.id);
        if (updated) setSelectedDept({ ...updated, ...deptData });
      } else {
        deptData.createdAt = new Date();
        const docRef = await addDoc(collection(db, 'departments'), deptData);
        const newDept = { id: docRef.id, ...deptData };
        showAlert('success', 'Department created successfully!');
        setSelectedDept(newDept);
        setViewMode('detail');
        setActiveDetailTab('overview');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving department:', error);
      showAlert('error', 'Error saving department: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    setLoading(true);
    try {
      const dept = departments.find(d => d.id === id);
      if (dept?.image) {
        try { await deleteObject(ref(storage, dept.image)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'departments', id));
      showAlert('success', 'Department deleted successfully!');
      if (selectedDept?.id === id) {
        setSelectedDept(null);
        setViewMode('list');
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      showAlert('error', 'Error deleting department: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleDepartmentSelect = (dept) => {
    setSelectedDept(dept);
    setViewMode('detail');
    setActiveDetailTab('overview');
    if (dept.notices) setNotices(dept.notices);
    else setNotices([]);
    if (dept.labs) setLabs(dept.labs);
    else setLabs([]);
    if (dept.gallery) setGalleryImages(dept.gallery);
    else setGalleryImages([]);
  };

  const goBackToList = () => {
    setViewMode('list');
    setSelectedDept(null);
  };

  // --- Notice Functions ---
  const handleNoticeFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        showAlert('error', 'Please upload a PDF file');
        return;
      }
      setNoticeForm({ ...noticeForm, file: file });
    }
  };

  const addNotice = async () => {
    if (!noticeForm.title.trim()) {
      showAlert('error', 'Please enter a notice title');
      return;
    }
    if (!noticeForm.file) {
      showAlert('error', 'Please upload a PDF file');
      return;
    }

    setIsUploading(true);
    setUploadingType('notice');
    try {
      const fileUrl = await handleFileUpload(noticeForm.file, 'departments/notices');
      const newNotice = {
        id: Date.now(),
        title: noticeForm.title,
        description: noticeForm.description || '',
        fileUrl: fileUrl,
        createdAt: new Date()
      };
      const updatedNotices = [...notices, newNotice];
      setNotices(updatedNotices);
      
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { notices: updatedNotices });
      }
      
      setNoticeForm({ title: '', description: '', file: null });
      const fileInput = document.getElementById('noticeFileInput');
      if (fileInput) fileInput.value = '';
      showAlert('success', 'Notice added successfully!');
    } catch (error) {
      showAlert('error', 'Error adding notice: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadingType('');
    }
  };

  const removeNotice = async (id) => {
    if (!window.confirm('Remove this notice?')) return;
    try {
      const notice = notices.find(n => n.id === id);
      if (notice?.fileUrl) {
        try { await deleteObject(ref(storage, notice.fileUrl)); } catch (e) {}
      }
      const updatedNotices = notices.filter(n => n.id !== id);
      setNotices(updatedNotices);
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { notices: updatedNotices });
      }
      showAlert('success', 'Notice removed successfully!');
    } catch (error) {
      showAlert('error', 'Error removing notice: ' + error.message);
    }
  };

  // --- Lab Functions ---
  const handleLabImagesChange = (e) => {
    handleMultipleImageChange(e, setLabImageFiles, setLabImagePreviews);
  };

  const addLab = async () => {
    if (!labForm.name.trim()) {
      showAlert('error', 'Please enter a lab name');
      return;
    }
    if (labImageFiles.length === 0) {
      showAlert('error', 'Please upload at least one image for the lab');
      return;
    }

    setIsUploading(true);
    setUploadingType('lab');
    try {
      const imageUrls = [];
      for (const file of labImageFiles) {
        const url = await handleFileUpload(file, 'departments/labs');
        imageUrls.push(url);
      }
      
      const newLab = {
        id: Date.now(),
        name: labForm.name,
        description: labForm.description || '',
        equipment: labForm.equipment || '',
        images: imageUrls,
        createdAt: new Date()
      };
      
      const updatedLabs = [...labs, newLab];
      setLabs(updatedLabs);
      
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { labs: updatedLabs });
      }
      
      setLabForm({ name: '', description: '', equipment: '', images: [] });
      setLabImageFiles([]);
      setLabImagePreviews([]);
      const fileInput = document.getElementById('labImageInput');
      if (fileInput) fileInput.value = '';
      showAlert('success', 'Lab added successfully!');
    } catch (error) {
      showAlert('error', 'Error adding lab: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadingType('');
    }
  };

  const removeLab = async (id) => {
    if (!window.confirm('Remove this lab?')) return;
    try {
      const lab = labs.find(l => l.id === id);
      if (lab?.images) {
        for (const img of lab.images) {
          try { await deleteObject(ref(storage, img)); } catch (e) {}
        }
      }
      const updatedLabs = labs.filter(l => l.id !== id);
      setLabs(updatedLabs);
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { labs: updatedLabs });
      }
      showAlert('success', 'Lab removed successfully!');
    } catch (error) {
      showAlert('error', 'Error removing lab: ' + error.message);
    }
  };

  // --- Gallery Functions ---
  const handleGalleryImagesChange = (e) => {
    handleMultipleImageChange(e, setGalleryFiles, setGalleryPreviews);
  };

  const addGalleryImages = async () => {
    if (galleryFiles.length === 0) {
      showAlert('error', 'Please select images to upload');
      return;
    }

    setIsUploading(true);
    setUploadingType('gallery');
    try {
      const imageUrls = [];
      for (const file of galleryFiles) {
        const url = await handleFileUpload(file, 'departments/gallery');
        imageUrls.push(url);
      }
      
      const newImages = imageUrls.map(url => ({
        id: Date.now() + Math.random(),
        image: url,
        uploadedAt: new Date()
      }));
      
      const updatedGallery = [...galleryImages, ...newImages];
      setGalleryImages(updatedGallery);
      
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { gallery: updatedGallery });
      }
      
      setGalleryFiles([]);
      setGalleryPreviews([]);
      const fileInput = document.getElementById('galleryImageInput');
      if (fileInput) fileInput.value = '';
      showAlert('success', 'Gallery images added successfully!');
    } catch (error) {
      showAlert('error', 'Error uploading gallery images: ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadingType('');
    }
  };

  const removeGalleryImage = async (id) => {
    if (!window.confirm('Remove this image?')) return;
    try {
      const img = galleryImages.find(i => i.id === id);
      if (img?.image) {
        try { await deleteObject(ref(storage, img.image)); } catch (e) {}
      }
      const updatedGallery = galleryImages.filter(i => i.id !== id);
      setGalleryImages(updatedGallery);
      if (selectedDept) {
        const deptRef = doc(db, 'departments', selectedDept.id);
        await updateDoc(deptRef, { gallery: updatedGallery });
      }
      showAlert('success', 'Image removed successfully!');
    } catch (error) {
      showAlert('error', 'Error removing image: ' + error.message);
    }
  };

  const getStats = () => {
    let totalSeats = 0;
    let totalFaculty = 0;
    departments.forEach(dept => {
      totalSeats += parseInt(dept.seats) || 0;
      totalFaculty += parseInt(dept.facultyCount) || 0;
    });
    return {
      totalDepartments: departments.length,
      totalSeats,
      totalFaculty,
      totalPrograms: departments.length * 3
    };
  };

  const stats = getStats();
  const getFacultyName = (facultyId) => {
    const facultyMember = faculty.find(f => f.id === facultyId);
    return facultyMember ? facultyMember.name : 'Not Assigned';
  };
  const getFacultyDetails = (facultyId) => faculty.find(f => f.id === facultyId) || null;
  const selectedFaculty = faculty.find(f => f.id === formData.headOfDepartment);

  // Render functions...
  const renderOverview = (dept) => {
    const hod = getFacultyDetails(dept.headOfDepartment);
    return (
      <div className="dept-tab-content">
        <div className="dept-overview-grid">
          <div className="overview-left">
            {dept.image && <img src={dept.image} alt={dept.name} className="dept-featured-image" />}
            <div className="dept-stats-mini">
              <div className="stat-mini"><i className="fas fa-users"></i><span>{dept.facultyCount || 'N/A'}</span><label>Faculty</label></div>
              <div className="stat-mini"><i className="fas fa-user-graduate"></i><span>{dept.seats || 'N/A'}</span><label>Seats</label></div>
              <div className="stat-mini"><i className="fas fa-clock"></i><span>{dept.duration || 'N/A'}</span><label>Duration</label></div>
            </div>
            <div className="dept-contact-info">
              <h4><i className="fas fa-address-card"></i> Contact</h4>
              {dept.email && <p><i className="fas fa-envelope"></i> {dept.email}</p>}
              {dept.phone && <p><i className="fas fa-phone"></i> {dept.phone}</p>}
              {dept.established && <p><i className="fas fa-calendar"></i> Est: {dept.established}</p>}
            </div>
          </div>
          <div className="overview-right">
            <h3>About {dept.name}</h3>
            <p>{dept.description || 'No description available.'}</p>
            {dept.hodMessage && (
              <div className="hod-message">
                <h4><i className="fas fa-quote-left"></i> Message from HOD</h4>
                <div className="hod-message-box">
                  {hod && (
                    <div className="hod-mini">
                      <img src={hod.image || 'https://ui-avatars.com/api/?name=' + hod.name} alt={hod.name} />
                      <div><strong>{hod.name}</strong><span>{hod.designation || 'Head of Department'}</span></div>
                    </div>
                  )}
                  <p>{dept.hodMessage}</p>
                </div>
              </div>
            )}
            {dept.achievements && (
              <div className="achievements-list">
                <h4><i className="fas fa-trophy"></i> Achievements</h4>
                <div className="achievement-tags">
                  {dept.achievements.split(',').map((ach, idx) => (
                    <span key={idx} className="achievement-tag">{ach.trim()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFaculty = (dept) => {
    const hod = getFacultyDetails(dept.headOfDepartment);
    const deptFaculty = faculty.filter(f => f.department === dept.name);
    return (
      <div className="dept-tab-content">
        <div className="faculty-list">
          <div className="faculty-hod">
            <h4><i className="fas fa-user-tie"></i> Head of Department</h4>
            {hod ? (
              <div className="hod-card">
                <div className="hod-avatar"><img src={hod.image || 'https://ui-avatars.com/api/?name=' + hod.name} alt={hod.name} /></div>
                <div className="hod-info">
                  <h5>{hod.name}</h5>
                  <p>{hod.designation || 'Head of Department'}</p>
                  {hod.email && <span className="hod-contact"><i className="fas fa-envelope"></i> {hod.email}</span>}
                  {hod.phone && <span className="hod-contact"><i className="fas fa-phone"></i> {hod.phone}</span>}
                </div>
              </div>
            ) : <div className="no-faculty">No HOD assigned</div>}
          </div>
          <div className="faculty-members">
            <h4><i className="fas fa-users"></i> Faculty ({deptFaculty.length})</h4>
            {deptFaculty.length > 0 ? (
              <div className="faculty-grid-mini">
                {deptFaculty.map((f) => (
                  <div className="faculty-mini-card" key={f.id}>
                    <img src={f.image || 'https://ui-avatars.com/api/?name=' + f.name} alt={f.name} />
                    <h5>{f.name}</h5>
                    <span>{f.designation || 'Faculty'}</span>
                  </div>
                ))}
              </div>
            ) : <p className="no-faculty">No faculty members assigned</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderNotices = () => {
    return (
      <div className="dept-tab-content">
        <div className="notices-section">
          <div className="section-header">
            <h3><i className="fas fa-bullhorn"></i> Notices</h3>
          </div>
          
          {/* Add Notice Form */}
          <div className="notice-add-form">
            <input
              type="text"
              placeholder="Notice Title *"
              value={noticeForm.title}
              onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
              className="notice-input"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={noticeForm.description}
              onChange={(e) => setNoticeForm({ ...noticeForm, description: e.target.value })}
              className="notice-input"
            />
            <div className="file-upload-wrapper small">
              <span>Browse PDF</span>
              <input
                id="noticeFileInput"
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleNoticeFileChange}
              />
            </div>
            <button className="add-btn" onClick={addNotice} disabled={isUploading && uploadingType === 'notice'}>
              {isUploading && uploadingType === 'notice' ? 'Uploading...' : <><i className="fas fa-plus"></i> Add Notice</>}
            </button>
          </div>

          {/* Notices List */}
          <div className="notices-list">
            {notices.length === 0 ? (
              <div className="empty-state-mini"><i className="fas fa-file-pdf"></i><p>No notices added yet</p></div>
            ) : (
              notices.map((notice) => (
                <div className="notice-item" key={notice.id}>
                  <div className="notice-info">
                    <div className="notice-title">
                      <i className="fas fa-file-pdf" style={{ color: '#f43f5e' }}></i>
                      {notice.title}
                      <span className="notice-date">{new Date(notice.createdAt).toLocaleDateString()}</span>
                    </div>
                    {notice.description && <div className="notice-desc">{notice.description}</div>}
                  </div>
                  <div className="notice-actions">
                    <a href={notice.fileUrl} target="_blank" rel="noopener noreferrer" className="view-btn">
                      <i className="fas fa-eye"></i>
                    </a>
                    <button className="remove-btn" onClick={() => removeNotice(notice.id)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderLabs = () => {
    return (
      <div className="dept-tab-content">
        <div className="labs-section">
          <div className="section-header">
            <h3><i className="fas fa-flask"></i> Labs</h3>
          </div>
          
          {/* Add Lab Form */}
          <div className="lab-add-form">
            <input
              type="text"
              placeholder="Lab Name *"
              value={labForm.name}
              onChange={(e) => setLabForm({ ...labForm, name: e.target.value })}
              className="lab-input"
            />
            <input
              type="text"
              placeholder="Equipment"
              value={labForm.equipment}
              onChange={(e) => setLabForm({ ...labForm, equipment: e.target.value })}
              className="lab-input"
            />
            <input
              type="text"
              placeholder="Description"
              value={labForm.description}
              onChange={(e) => setLabForm({ ...labForm, description: e.target.value })}
              className="lab-input"
            />
            <div className="file-upload-wrapper small">
              <span>Upload Images</span>
              <input
                id="labImageInput"
                type="file"
                accept="image/*"
                multiple
                onChange={handleLabImagesChange}
              />
            </div>
            {labImagePreviews.length > 0 && (
              <div className="preview-thumbnails">
                {labImagePreviews.map((preview, idx) => (
                  <img key={idx} src={preview} alt={`Lab ${idx}`} className="thumbnail" />
                ))}
              </div>
            )}
            <button className="add-btn" onClick={addLab} disabled={isUploading && uploadingType === 'lab'}>
              {isUploading && uploadingType === 'lab' ? 'Uploading...' : <><i className="fas fa-plus"></i> Add Lab</>}
            </button>
          </div>

          {/* Labs List */}
          <div className="labs-grid">
            {labs.length === 0 ? (
              <div className="empty-state-mini"><i className="fas fa-flask"></i><p>No labs added yet</p></div>
            ) : (
              labs.map((lab) => (
                <div className="lab-card" key={lab.id}>
                  <div className="lab-images">
                    {lab.images && lab.images.slice(0, 3).map((img, idx) => (
                      <img key={idx} src={img} alt={lab.name} className="lab-image-thumb" />
                    ))}
                    {lab.images && lab.images.length > 3 && (
                      <div className="more-images">+{lab.images.length - 3}</div>
                    )}
                  </div>
                  <div className="lab-info">
                    <h4>{lab.name}</h4>
                    {lab.equipment && <div className="lab-equipment"><i className="fas fa-tools"></i> {lab.equipment}</div>}
                    {lab.description && <p>{lab.description}</p>}
                    <button className="remove-btn" onClick={() => removeLab(lab.id)}>
                      <i className="fas fa-trash"></i> Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGallery = () => {
    return (
      <div className="dept-tab-content">
        <div className="gallery-section">
          <div className="section-header">
            <h3><i className="fas fa-images"></i> Gallery</h3>
            <div className="gallery-add-form">
              <div className="file-upload-wrapper small">
                <span>Upload Images</span>
                <input
                  id="galleryImageInput"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryImagesChange}
                />
              </div>
              {galleryPreviews.length > 0 && (
                <div className="preview-thumbnails">
                  {galleryPreviews.slice(0, 4).map((preview, idx) => (
                    <img key={idx} src={preview} alt={`Gallery ${idx}`} className="thumbnail" />
                  ))}
                  {galleryPreviews.length > 4 && <span className="more-count">+{galleryPreviews.length - 4}</span>}
                </div>
              )}
              <button className="add-btn" onClick={addGalleryImages} disabled={isUploading && uploadingType === 'gallery'}>
                {isUploading && uploadingType === 'gallery' ? 'Uploading...' : <><i className="fas fa-plus"></i> Add Images</>}
              </button>
            </div>
          </div>

          <div className="gallery-grid">
            {galleryImages.length === 0 ? (
              <div className="empty-state-mini"><i className="fas fa-images"></i><p>No gallery images yet</p></div>
            ) : (
              galleryImages.map((img) => (
                <div className="gallery-item" key={img.id}>
                  <img src={img.image} alt="Gallery" />
                  <div className="gallery-overlay">
                    <button className="remove-btn" onClick={() => removeGalleryImage(img.id)}>
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  // CSS Styles
  const styles = `
    .departments-page { animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }
    .dept-selector-group { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
    .dept-select-btn {
      padding: 10px 24px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      background: rgba(255, 255, 255, 0.4);
      border-radius: 30px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .dept-select-btn:hover { background: rgba(255, 255, 255, 0.7); transform: translateY(-2px); border-color: #0ea5e9; }
    .dept-select-btn.active {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border-color: #0ea5e9;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
    }
    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }
    .btn-action-trigger:hover { transform: translateY(-3px); }
    .btn-blue { background: linear-gradient(135deg, #0ea5e9, #0284c7); color: white; }
    .btn-blue:hover { box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4); }
    .btn-back {
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      color: #334155;
      padding: 8px 16px;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .btn-back:hover { background: #ffffff; border-color: #334155; }

    .dept-detail-view {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      overflow: hidden;
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      animation: structuralSlideUp 0.4s ease;
    }
    .dept-detail-header {
      padding: 24px 32px;
      background: linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(14, 165, 233, 0.02));
      border-bottom: 1px solid rgba(14, 165, 233, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }
    .dept-detail-header-left { display: flex; align-items: center; gap: 16px; }
    .dept-detail-header-left .dept-icon {
      width: 56px;
      height: 56px;
      border-radius: 12px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 24px;
    }
    .dept-detail-header-left h2 { font-family: 'Space Grotesk', sans-serif; font-size: 1.4rem; color: #0f172a; margin: 0; }
    .dept-detail-header-left .dept-sub { font-size: 0.85rem; color: #64748b; }
    .dept-detail-actions { display: flex; gap: 8px; }
    .dept-detail-actions button {
      padding: 8px 16px;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.8rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn-primary { background: linear-gradient(135deg, #0ea5e9, #0284c7); color: white; }
    .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(14, 165, 233, 0.3); }
    .btn-danger { background: #f43f5e; color: white; }
    .btn-danger:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(244, 63, 94, 0.3); }

    .dept-detail-tabs {
      display: flex;
      gap: 4px;
      padding: 0 32px;
      background: rgba(255, 255, 255, 0.3);
      border-bottom: 1px solid rgba(14, 165, 233, 0.1);
      overflow-x: auto;
    }
    .dept-detail-tabs button {
      padding: 14px 24px;
      background: none;
      border: none;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #64748b;
      transition: all 0.3s ease;
      position: relative;
      white-space: nowrap;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .dept-detail-tabs button:hover { color: #0f172a; }
    .dept-detail-tabs button.active { color: #0ea5e9; }
    .dept-detail-tabs button.active::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 24px;
      right: 24px;
      height: 3px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      border-radius: 3px 3px 0 0;
    }
    .dept-tab-content { padding: 32px; }

    /* Overview */
    .dept-overview-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 32px; }
    .overview-left { display: flex; flex-direction: column; gap: 20px; }
    .dept-featured-image { width: 100%; height: 280px; object-fit: cover; border-radius: 12px; border: 2px solid rgba(255, 255, 255, 0.6); }
    .dept-stats-mini { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .stat-mini { background: rgba(255, 255, 255, 0.3); padding: 16px; border-radius: 12px; text-align: center; border: 1px solid rgba(255, 255, 255, 0.4); }
    .stat-mini i { font-size: 20px; color: #0ea5e9; display: block; margin-bottom: 4px; }
    .stat-mini span { font-size: 1.2rem; font-weight: 700; color: #0f172a; display: block; }
    .stat-mini label { font-size: 0.7rem; color: #64748b; font-weight: 600; text-transform: uppercase; }
    .dept-contact-info { background: rgba(255, 255, 255, 0.3); padding: 16px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .dept-contact-info h4 { font-size: 0.9rem; color: #0f172a; margin-bottom: 8px; }
    .dept-contact-info p { font-size: 0.85rem; color: #334155; margin: 4px 0; }
    .dept-contact-info p i { color: #0ea5e9; width: 20px; }
    .overview-right h3 { font-family: 'Space Grotesk', sans-serif; font-size: 1.2rem; color: #0f172a; margin-bottom: 12px; }
    .overview-right > p { color: #334155; line-height: 1.6; font-size: 0.95rem; }
    .hod-message { margin-top: 20px; }
    .hod-message h4 { font-size: 0.95rem; color: #0f172a; margin-bottom: 12px; }
    .hod-message h4 i { color: #0ea5e9; margin-right: 8px; }
    .hod-message-box { background: rgba(255, 255, 255, 0.3); padding: 20px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .hod-mini { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
    .hod-mini img { width: 48px; height: 48px; border-radius: 50%; object-fit: cover; }
    .hod-mini strong { display: block; color: #0f172a; }
    .hod-mini span { font-size: 0.8rem; color: #64748b; }
    .hod-message-box p { color: #334155; font-size: 0.9rem; line-height: 1.6; font-style: italic; }
    .achievements-list { margin-top: 20px; }
    .achievements-list h4 { font-size: 0.95rem; color: #0f172a; margin-bottom: 8px; }
    .achievements-list h4 i { color: #f59e0b; margin-right: 8px; }
    .achievement-tags { display: flex; flex-wrap: wrap; gap: 8px; }
    .achievement-tag { background: rgba(245, 158, 11, 0.12); color: #f59e0b; padding: 4px 14px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; border: 1px solid rgba(245, 158, 11, 0.2); }

    /* Faculty */
    .faculty-list { display: grid; grid-template-columns: 1fr 2fr; gap: 24px; }
    .faculty-hod h4, .faculty-members h4 { font-size: 1rem; color: #0f172a; margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
    .hod-card { display: flex; align-items: center; gap: 16px; background: rgba(255, 255, 255, 0.3); padding: 20px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .hod-avatar { width: 80px; height: 80px; border-radius: 50%; overflow: hidden; background: rgba(14, 165, 233, 0.1); flex-shrink: 0; }
    .hod-avatar img { width: 100%; height: 100%; object-fit: cover; }
    .hod-info h5 { font-family: 'Space Grotesk', sans-serif; font-size: 1.1rem; color: #0f172a; }
    .hod-info p { color: #64748b; font-size: 0.85rem; }
    .hod-contact { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; color: #0ea5e9; margin-top: 4px; margin-right: 12px; }
    .faculty-grid-mini { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
    .faculty-mini-card { background: rgba(255, 255, 255, 0.3); border-radius: 12px; padding: 16px; text-align: center; border: 1px solid rgba(255, 255, 255, 0.4); transition: all 0.3s ease; }
    .faculty-mini-card:hover { transform: translateY(-2px); box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06); }
    .faculty-mini-card img { width: 60px; height: 60px; border-radius: 50%; object-fit: cover; margin-bottom: 8px; }
    .faculty-mini-card h5 { font-size: 0.85rem; color: #0f172a; font-weight: 700; margin: 4px 0; }
    .faculty-mini-card span { font-size: 0.75rem; color: #64748b; }
    .no-faculty { color: #94a3b8; font-size: 0.85rem; padding: 20px; text-align: center; }

    /* Notices */
    .notices-section, .labs-section, .gallery-section { display: flex; flex-direction: column; gap: 20px; }
    .section-header { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
    .section-header h3 { font-family: 'Space Grotesk', sans-serif; font-size: 1.1rem; color: #0f172a; display: flex; align-items: center; gap: 8px; }
    .section-header h3 i { color: #0ea5e9; }
    .notice-add-form { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; background: rgba(255, 255, 255, 0.3); padding: 16px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .notice-input { padding: 10px 14px; border: 2px solid rgba(14, 165, 233, 0.15); border-radius: 8px; font-size: 0.85rem; background: rgba(255, 255, 255, 0.6); flex: 1; min-width: 150px; }
    .notice-input:focus { border-color: #0ea5e9; outline: none; }
    .file-upload-wrapper.small { position: relative; padding: 8px 16px; border: 2px dashed rgba(14, 165, 233, 0.3); border-radius: 8px; cursor: pointer; background: rgba(255, 255, 255, 0.3); font-size: 0.8rem; color: #64748b; }
    .file-upload-wrapper.small:hover { border-color: #0ea5e9; background: rgba(255, 255, 255, 0.5); }
    .file-upload-wrapper.small input { position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
    .add-btn {
      padding: 10px 20px;
      background: rgba(14, 165, 233, 0.1);
      border: 1px solid rgba(14, 165, 233, 0.2);
      border-radius: 8px;
      color: #0ea5e9;
      cursor: pointer;
      font-weight: 600;
      font-size: 0.85rem;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .add-btn:hover:not(:disabled) { background: #0ea5e9; color: white; }
    .add-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .notices-list { display: flex; flex-direction: column; gap: 8px; }
    .notice-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: rgba(255, 255, 255, 0.3); border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .notice-item .notice-info { flex: 1; }
    .notice-item .notice-title { font-weight: 600; font-size: 0.9rem; color: #0f172a; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .notice-item .notice-title i { font-size: 1.1rem; }
    .notice-item .notice-date { font-size: 0.7rem; color: #64748b; font-weight: 400; }
    .notice-item .notice-desc { font-size: 0.8rem; color: #64748b; margin-top: 2px; }
    .notice-actions { display: flex; gap: 8px; align-items: center; }
    .view-btn { padding: 6px 10px; background: rgba(14, 165, 233, 0.1); border: 1px solid rgba(14, 165, 233, 0.2); border-radius: 6px; color: #0ea5e9; cursor: pointer; transition: all 0.2s; text-decoration: none; font-size: 0.8rem; }
    .view-btn:hover { background: #0ea5e9; color: white; }
    .remove-btn { padding: 6px 10px; background: rgba(244, 63, 94, 0.1); border: 1px solid rgba(244, 63, 94, 0.2); border-radius: 6px; color: #f43f5e; cursor: pointer; transition: all 0.2s; font-size: 0.8rem; }
    .remove-btn:hover { background: #f43f5e; color: white; }

    /* Labs */
    .lab-add-form { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; background: rgba(255, 255, 255, 0.3); padding: 16px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.4); }
    .lab-input { padding: 10px 14px; border: 2px solid rgba(14, 165, 233, 0.15); border-radius: 8px; font-size: 0.85rem; background: rgba(255, 255, 255, 0.6); flex: 1; min-width: 150px; }
    .lab-input:focus { border-color: #0ea5e9; outline: none; }
    .preview-thumbnails { display: flex; gap: 6px; flex-wrap: wrap; }
    .thumbnail { width: 50px; height: 50px; object-fit: cover; border-radius: 6px; border: 2px solid rgba(14, 165, 233, 0.2); }
    .more-count { display: flex; align-items: center; justify-content: center; width: 50px; height: 50px; background: rgba(14, 165, 233, 0.1); border-radius: 6px; font-weight: 700; color: #0ea5e9; }
    .labs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px; }
    .lab-card { background: rgba(255, 255, 255, 0.3); border-radius: 12px; overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.4); transition: all 0.3s ease; }
    .lab-card:hover { transform: translateY(-4px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08); }
    .lab-images { display: flex; height: 150px; overflow: hidden; }
    .lab-image-thumb { flex: 1; object-fit: cover; }
    .more-images { display: flex; align-items: center; justify-content: center; width: 60px; background: rgba(0, 0, 0, 0.5); color: white; font-weight: 700; font-size: 0.9rem; }
    .lab-info { padding: 16px; }
    .lab-info h4 { font-family: 'Space Grotesk', sans-serif; font-size: 1rem; color: #0f172a; margin-bottom: 4px; }
    .lab-info p { color: #64748b; font-size: 0.85rem; line-height: 1.5; margin-bottom: 8px; }
    .lab-equipment { display: flex; align-items: center; gap: 8px; font-size: 0.8rem; color: #334155; background: rgba(14, 165, 233, 0.08); padding: 6px 12px; border-radius: 6px; margin-bottom: 8px; }
    .lab-equipment i { color: #0ea5e9; }

    /* Gallery */
    .gallery-add-form { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
    .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
    .gallery-item { position: relative; border-radius: 12px; overflow: hidden; cursor: pointer; aspect-ratio: 1; }
    .gallery-item img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; }
    .gallery-item:hover img { transform: scale(1.05); }
    .gallery-overlay {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
    }
    .gallery-item:hover .gallery-overlay { opacity: 1; }
    .gallery-overlay .remove-btn { background: #f43f5e; color: white; border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; font-size: 1rem; transition: all 0.2s; display: flex; align-items: center; justify-content: center; }
    .gallery-overlay .remove-btn:hover { transform: scale(1.1); }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
    }
    .empty-state i { font-size: 54px; color: #64748b; margin-bottom: 16px; display: block; }
    .empty-state h3 { font-family: 'Space Grotesk', sans-serif; font-size: 1.2rem; margin-bottom: 6px; color: #0f172a; }
    .empty-state p { color: #64748b; font-size: 0.88rem; }
    .empty-state-mini { text-align: center; padding: 40px; color: #94a3b8; }
    .empty-state-mini i { font-size: 3rem; display: block; margin-bottom: 12px; color: #cbd5e1; }

    /* Alert */
    .custom-alert {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 100000;
      padding: 16px 24px;
      border-radius: 12px;
      background: white;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 12px;
      animation: slideInRight 0.3s ease;
      border-left: 5px solid #10b981;
      max-width: 450px;
    }
    .custom-alert.success { border-left-color: #10b981; }
    .custom-alert.error { border-left-color: #f43f5e; }
    .custom-alert .alert-icon { font-size: 1.2rem; }
    .custom-alert .alert-icon.success { color: #10b981; }
    .custom-alert .alert-icon.error { color: #f43f5e; }
    .custom-alert .alert-message { font-weight: 600; color: #0f172a; flex: 1; }
    .custom-alert .alert-close { background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 1.1rem; padding: 4px; }
    .custom-alert .alert-close:hover { color: #0f172a; }

    @keyframes slideInRight {
      from { transform: translateX(100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }

    /* Modal */
    .modal-overlay {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(10px);
      z-index: 99999;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px 80px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }
    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 750px;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
      margin-bottom: 40px;
    }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .modal-viewport-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid rgba(14, 165, 233, 0.1); }
    .modal-viewport-header h2 { font-family: 'Space Grotesk', sans-serif; font-size: 1.4rem; color: #0f172a; display: flex; align-items: center; gap: 12px; }
    .modal-viewport-header h2 i { color: #0ea5e9; }
    .modal-close-btn { width: 40px; height: 40px; background: rgba(244, 63, 94, 0.1); border: 1px solid rgba(244, 63, 94, 0.25); border-radius: 12px; color: #f43f5e; cursor: pointer; font-size: 1.2rem; transition: all 0.2s; display: flex; align-items: center; justify-content: center; }
    .modal-close-btn:hover { background: #f43f5e; color: white; transform: rotate(90deg); }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
    .form-group { margin-bottom: 4px; }
    .form-group.full-width { grid-column: 1 / -1; }
    .form-group label { display: block; margin-bottom: 8px; font-weight: 700; font-size: 0.85rem; color: #334155; }
    .form-group label .required { color: #f43f5e; margin-left: 4px; }
    .form-group input, .form-group textarea, .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }
    .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }
    .form-group textarea { resize: vertical; min-height: 80px; }
    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }
    .file-upload-wrapper:hover { border-color: #0ea5e9; background: rgba(255, 255, 255, 0.5); }
    .file-upload-wrapper input[type="file"] { position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
    .file-upload-wrapper .upload-icon { font-size: 1.5rem; color: #0ea5e9; margin-bottom: 4px; display: block; }
    .file-upload-wrapper .upload-text { color: #64748b; font-size: 0.8rem; font-weight: 600; }
    .file-upload-wrapper .upload-text span { color: #0ea5e9; font-weight: 700; }
    .image-preview-wrapper { margin-top: 12px; position: relative; display: inline-block; border-radius: 12px; overflow: hidden; border: 2px solid rgba(14, 165, 233, 0.2); }
    .image-preview-wrapper img { width: 120px; height: 120px; object-fit: cover; display: block; }
    .image-preview-wrapper .remove-image { position: absolute; top: 4px; right: 4px; background: rgba(244, 63, 94, 0.9); color: white; border: none; border-radius: 50%; width: 24px; height: 24px; cursor: pointer; font-size: 12px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
    .image-preview-wrapper .remove-image:hover { transform: scale(1.1); background: #f43f5e; }
    .upload-progress { margin-top: 12px; background: rgba(14, 165, 233, 0.1); border-radius: 8px; height: 6px; overflow: hidden; }
    .upload-progress .progress-bar { height: 100%; background: linear-gradient(135deg, #0ea5e9, #0284c7); transition: width 0.3s ease; border-radius: 8px; }
    .faculty-select-wrapper { position: relative; }
    .faculty-select-trigger {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 12px;
      transition: all 0.3s ease;
    }
    .faculty-select-trigger:hover { border-color: #0ea5e9; }
    .faculty-select-trigger .faculty-avatar-small { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; background: #e2e8f0; }
    .faculty-select-trigger .faculty-name { flex: 1; font-weight: 600; color: #0f172a; }
    .faculty-select-trigger .faculty-placeholder { flex: 1; color: #94a3b8; font-weight: 400; }
    .faculty-select-trigger .dropdown-arrow { color: #94a3b8; transition: transform 0.3s ease; }
    .faculty-select-trigger .dropdown-arrow.open { transform: rotate(180deg); }
    .faculty-dropdown-list {
      position: absolute;
      top: calc(100% + 8px);
      left: 0; right: 0;
      background: white;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      max-height: 250px;
      overflow-y: auto;
      z-index: 1000;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      display: none;
    }
    .faculty-dropdown-list.open { display: block; }
    .faculty-dropdown-item { padding: 10px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: all 0.2s ease; border-bottom: 1px solid rgba(14, 165, 233, 0.05); }
    .faculty-dropdown-item:last-child { border-bottom: none; }
    .faculty-dropdown-item:hover { background: rgba(14, 165, 233, 0.05); }
    .faculty-dropdown-item.selected { background: rgba(14, 165, 233, 0.1); }
    .faculty-dropdown-item .faculty-avatar-small { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
    .faculty-dropdown-item .faculty-info { flex: 1; }
    .faculty-dropdown-item .faculty-info .name { font-weight: 600; color: #0f172a; font-size: 0.9rem; }
    .faculty-dropdown-item .faculty-info .designation { font-size: 0.75rem; color: #64748b; }
    .faculty-dropdown-item .check-icon { color: #0ea5e9; }
    .modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 24px; padding-top: 16px; border-top: 2px solid rgba(14, 165, 233, 0.1); }
    .btn-cancel { padding: 10px 24px; background: rgba(255, 255, 255, 0.4); border: 2px solid rgba(14, 165, 233, 0.2); border-radius: 12px; color: #334155; cursor: pointer; font-weight: 700; font-size: 0.9rem; transition: all 0.3s ease; }
    .btn-cancel:hover { background: #ffffff; border-color: #334155; transform: translateY(-2px); }
    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }
    .btn-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4); }
    .btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }
    .btn-submit .spinner { width: 18px; height: 18px; border: 2px solid rgba(255, 255, 255, 0.3); border-top-color: white; border-radius: 50%; animation: spin 0.6s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }

    @media (max-width: 1024px) {
      .dept-overview-grid { grid-template-columns: 1fr; }
      .faculty-list { grid-template-columns: 1fr; }
    }
    @media (max-width: 768px) {
      .management-toolbar { flex-direction: column; align-items: stretch; }
      .dept-selector-group { justify-content: center; }
      .dept-select-btn { padding: 8px 16px; font-size: 0.8rem; }
      .dept-detail-header { flex-direction: column; align-items: stretch; padding: 16px 20px; }
      .dept-detail-header-left { flex-direction: column; align-items: flex-start; }
      .dept-detail-tabs { padding: 0 16px; }
      .dept-detail-tabs button { padding: 10px 16px; font-size: 0.75rem; }
      .dept-tab-content { padding: 16px 20px; }
      .dept-stats-mini { grid-template-columns: 1fr 1fr; }
      .form-grid { grid-template-columns: 1fr; }
      .modal-viewport-box { padding: 24px; margin: 10px; }
      .modal-viewport-header h2 { font-size: 1.2rem; }
      .modal-footer { flex-direction: column; }
      .modal-footer button { width: 100%; justify-content: center; }
      .notice-add-form, .lab-add-form { flex-direction: column; align-items: stretch; }
      .notice-input, .lab-input { width: 100%; }
    }
    @media (max-width: 480px) {
      .dept-selector-group { flex-direction: column; width: 100%; }
      .dept-select-btn { width: 100%; justify-content: center; }
      .faculty-grid-mini { grid-template-columns: 1fr 1fr; }
      .gallery-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
    }
  `;

  if (loading && departments.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <style>{styles}</style>
      <div className="departments-page">
        {/* Alert */}
        {alert.show && (
          <div className={`custom-alert ${alert.type}`}>
            <i className={`fas ${alert.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} alert-icon ${alert.type}`}></i>
            <span className="alert-message">{alert.message}</span>
            <button className="alert-close" onClick={() => setAlert({ show: false, type: 'success', message: '' })}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        <div className="welcome-hero">
          <h1>🏛️ Departments Management</h1>
          <p>Manage department information, faculty, notices, labs, and gallery.</p>
          <div className="hero-meta-date"><i className="far fa-clock"></i> {currentTime}</div>
        </div>

        <div className="stats-matrix">
          <div className="matrix-card card-blue"><div><div className="matrix-value">{stats.totalDepartments}</div><div className="matrix-label">Total Departments</div></div><div className="matrix-icon-box"><i className="fas fa-building"></i></div></div>
          <div className="matrix-card card-emerald"><div><div className="matrix-value">{stats.totalSeats}+</div><div className="matrix-label">Total Seats</div></div><div className="matrix-icon-box"><i className="fas fa-users"></i></div></div>
          <div className="matrix-card card-amber"><div><div className="matrix-value">{stats.totalFaculty}+</div><div className="matrix-label">Faculty Members</div></div><div className="matrix-icon-box"><i className="fas fa-user-tie"></i></div></div>
          <div className="matrix-card card-purple"><div><div className="matrix-value">{stats.totalPrograms}+</div><div className="matrix-label">Programs Offered</div></div><div className="matrix-icon-box"><i className="fas fa-graduation-cap"></i></div></div>
        </div>

        <div className="management-toolbar">
          <div className="dept-selector-group">
            {departments.map((dept) => (
              <button
                key={dept.id}
                className={`dept-select-btn ${selectedDept?.id === dept.id && viewMode === 'detail' ? 'active' : ''}`}
                onClick={() => handleDepartmentSelect(dept)}
              >
                <i className="fas fa-building"></i> {dept.name}
              </button>
            ))}
            <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
              <i className="fas fa-plus"></i> Add New
            </button>
          </div>
        </div>

        {viewMode === 'detail' && selectedDept ? (
          <div className="dept-detail-view">
            <div className="dept-detail-header">
              <div className="dept-detail-header-left">
                <div className="dept-icon"><i className="fas fa-building"></i></div>
                <div>
                  <h2>{selectedDept.name}</h2>
                  <span className="dept-sub">{selectedDept.facultyCount || '0'} Faculty • {selectedDept.seats || '0'} Seats • {selectedDept.duration || 'N/A'}</span>
                </div>
              </div>
              <div className="dept-detail-actions">
                <button className="btn-back" onClick={goBackToList}><i className="fas fa-arrow-left"></i> Back</button>
                <button className="btn-primary" onClick={() => openEditModal(selectedDept)}><i className="fas fa-edit"></i> Edit</button>
                <button className="btn-danger" onClick={() => handleDelete(selectedDept.id)}><i className="fas fa-trash"></i> Delete</button>
              </div>
            </div>

            <div className="dept-detail-tabs">
              <button className={activeDetailTab === 'overview' ? 'active' : ''} onClick={() => setActiveDetailTab('overview')}><i className="fas fa-info-circle"></i> Overview</button>
              <button className={activeDetailTab === 'faculty' ? 'active' : ''} onClick={() => setActiveDetailTab('faculty')}><i className="fas fa-user-tie"></i> Faculty</button>
              <button className={activeDetailTab === 'notices' ? 'active' : ''} onClick={() => setActiveDetailTab('notices')}><i className="fas fa-bullhorn"></i> Notices</button>
              <button className={activeDetailTab === 'labs' ? 'active' : ''} onClick={() => setActiveDetailTab('labs')}><i className="fas fa-flask"></i> Labs</button>
              <button className={activeDetailTab === 'gallery' ? 'active' : ''} onClick={() => setActiveDetailTab('gallery')}><i className="fas fa-images"></i> Gallery</button>
            </div>

            {activeDetailTab === 'overview' && renderOverview(selectedDept)}
            {activeDetailTab === 'faculty' && renderFaculty(selectedDept)}
            {activeDetailTab === 'notices' && renderNotices()}
            {activeDetailTab === 'labs' && renderLabs()}
            {activeDetailTab === 'gallery' && renderGallery()}
          </div>
        ) : (
          <div className="empty-state">
            <i className="fas fa-building"></i>
            <h3>Select a Department</h3>
            <p>Click on a department above to view its details, or create a new one.</p>
          </div>
        )}

        {/* Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2><i className={`fas ${editingDept ? 'fa-edit' : 'fa-plus-circle'}`}></i> {editingDept ? 'Edit Department' : 'Create New Department'}</h2>
                <button className="modal-close-btn" onClick={closeModal}><i className="fas fa-times"></i></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Department Name <span className="required">*</span></label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Computer Science" required />
                  </div>
                  <div className="form-group full-width">
                    <label>Description <span className="required">*</span></label>
                    <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows="3" placeholder="Brief description of the department..." required />
                  </div>
                  <div className="form-group"><label>Seats / Intake</label><input type="text" value={formData.seats} onChange={(e) => setFormData({ ...formData, seats: e.target.value })} placeholder="e.g., 60 Seats" /></div>
                  <div className="form-group"><label>Duration</label><input type="text" value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="e.g., 4 Years" /></div>
                  <div className="form-group"><label>Faculty Count</label><input type="text" value={formData.facultyCount} onChange={(e) => setFormData({ ...formData, facultyCount: e.target.value })} placeholder="e.g., 25+" /></div>
                  <div className="form-group">
                    <label>Head of Department</label>
                    <div className="faculty-select-wrapper">
                      <div className="faculty-select-trigger" onClick={() => setShowFacultyDropdown(!showFacultyDropdown)}>
                        {selectedFaculty ? (
                          <>
                            <img src={selectedFaculty.image || 'https://ui-avatars.com/api/?name=' + selectedFaculty.name} alt={selectedFaculty.name} className="faculty-avatar-small" />
                            <span className="faculty-name">{selectedFaculty.name}</span>
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{selectedFaculty.designation || 'Faculty'}</span>
                          </>
                        ) : (
                          <span className="faculty-placeholder">Select Head of Department</span>
                        )}
                        <i className={`fas fa-chevron-down dropdown-arrow ${showFacultyDropdown ? 'open' : ''}`}></i>
                      </div>
                      <div className={`faculty-dropdown-list ${showFacultyDropdown ? 'open' : ''}`}>
                        <div className="faculty-dropdown-item" onClick={() => { setFormData({ ...formData, headOfDepartment: '' }); setShowFacultyDropdown(false); }}>
                          <span className="faculty-info"><span className="name">None</span></span>
                        </div>
                        {faculty.map((f) => (
                          <div key={f.id} className={`faculty-dropdown-item ${formData.headOfDepartment === f.id ? 'selected' : ''}`} onClick={() => { setFormData({ ...formData, headOfDepartment: f.id }); setShowFacultyDropdown(false); }}>
                            <img src={f.image || 'https://ui-avatars.com/api/?name=' + f.name} alt={f.name} className="faculty-avatar-small" />
                            <div className="faculty-info"><div className="name">{f.name}</div><div className="designation">{f.designation || 'Faculty'}</div></div>
                            {formData.headOfDepartment === f.id && <i className="fas fa-check-circle check-icon"></i>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="form-group"><label>Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="dept@college.edu" /></div>
                  <div className="form-group"><label>Phone</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 1234567890" /></div>
                  <div className="form-group"><label>Established Year</label><input type="text" value={formData.established} onChange={(e) => setFormData({ ...formData, established: e.target.value })} placeholder="e.g., 2010" /></div>
                  <div className="form-group"><label>Achievements (comma separated)</label><input type="text" value={formData.achievements} onChange={(e) => setFormData({ ...formData, achievements: e.target.value })} placeholder="NIRF Ranked, 100% Placement" /></div>
                  <div className="form-group full-width">
                    <label>Message from HOD</label>
                    <textarea value={formData.hodMessage} onChange={(e) => setFormData({ ...formData, hodMessage: e.target.value })} rows="3" placeholder="Welcome message from the Head of Department..." />
                  </div>
                  <div className="form-group full-width">
                    <label>Department Image</label>
                    <div className="file-upload-wrapper">
                      <i className="fas fa-cloud-upload-alt upload-icon"></i>
                      <div className="upload-text">Drag & drop or <span>browse</span> to upload image</div>
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                    </div>
                    {imagePreview && (
                      <div className="image-preview-wrapper">
                        <img src={imagePreview} alt="Preview" />
                        <button type="button" className="remove-image" onClick={() => { setImagePreview(''); setImageFile(null); setFormData({ ...formData, image: '' }); }}><i className="fas fa-times"></i></button>
                      </div>
                    )}
                  </div>
                </div>
                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress"><div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div></div>
                )}
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}><i className="fas fa-times"></i> Cancel</button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? <><span className="spinner"></span> Uploading...</> : <><i className="fas fa-save"></i> {editingDept ? 'Update Department' : 'Create Department'}</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Departments;